// ProductionOrderDetail.js - dedicated page for one ProductionOrder, so
// the full ProductionCycleTimelineCard is always visible without needing
// to expand a row in the list (Admin/src/Pages/ProductionOrders/).
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { UilArrowLeft } from "@iconscout/react-unicons";
import "../Stock/Stock.css";
import { userRequest } from "../../RequestMethods";
import { getColorDisplayName } from "../../utils/colorNames";
import { TERMINAL_STATUS, nextStatus } from "../../Data/productionStatuses";
import { ProductionCycleTimelineCard } from "../../Components/ProductionCycleTimeline/ProductionCycleTimeline";

const ProductionOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [productionOrder, setProductionOrder] = useState(null);
  const [stepLog, setStepLog] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const res = await userRequest.get(`production-orders/${id}`);
      setProductionOrder(res.data.productionOrder);
      setStepLog(res.data.stepLog || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement de l'ordre de production.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleAdvance = async () => {
    const to = nextStatus(productionOrder.status);
    if (!to) return;
    try {
      await userRequest.post(`production-orders/${id}/advance`, { toStatus: to });
      toast.success(to === TERMINAL_STATUS ? "Production terminée - stock crédité." : "Étape suivante.");
      fetchDetail();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur lors du changement de statut.");
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Annuler cet ordre de production ? Le matériel réservé sera libéré.")) return;
    try {
      await userRequest.delete(`production-orders/${id}`);
      toast.success("Ordre de production annulé.");
      navigate("/production-orders");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur lors de l'annulation.");
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="loading-spinner-custom"></div></div>;
  }

  if (!productionOrder) {
    return (
      <div className="Stock">
        <p>Ordre de production introuvable.</p>
        <button className="action-icon-btn btn-edit" onClick={() => navigate("/production-orders")}>
          <UilArrowLeft size={14} /> Retour
        </button>
      </div>
    );
  }

  const po = productionOrder;

  return (
    <div className="Stock">
      <button
        className="action-icon-btn btn-edit"
        style={{ alignSelf: "flex-start" }}
        onClick={() => navigate("/production-orders")}
      >
        <UilArrowLeft size={14} /> Retour à la production
      </button>

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 style={{ margin: 0 }}>
            {po.productTitle}{po.designId?.title ? ` — ${po.designId.title}` : ""}
          </h1>
          <div className="field-note" style={{ marginTop: "6px" }}>
            {po.color ? getColorDisplayName(po.color) : "—"} / {po.size} &nbsp;·&nbsp; Qté {po.quantity}
            {po.assignedTo ? ` · Assigné à ${po.assignedTo.name || ""} ${po.assignedTo.surname || ""}`.trim() : ""}
            {po.workshopRef ? ` · Réf. atelier ${po.workshopRef}` : ""}
            {po.isRush ? " · ⚡ Urgent" : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {po.status !== TERMINAL_STATUS && (
            <button className="action-icon-btn btn-primary" onClick={handleAdvance}>
              → {nextStatus(po.status)}
            </button>
          )}
          {po.status !== TERMINAL_STATUS && (
            <button className="action-icon-btn btn-delete" onClick={handleCancel}>Annuler</button>
          )}
        </div>
      </div>

      <section className="stock-section">
        <ProductionCycleTimelineCard
          status={po.status}
          hasDesign={!!po.designId}
          order={po.orderId || null}
        />
      </section>

      <section className="stock-section">
        <h2>Historique</h2>
        {stepLog.length === 0 ? (
          <p>Aucun historique.</p>
        ) : (
          stepLog.map((log) => (
            <div key={log._id} className="field-note" style={{ marginBottom: "6px" }}>
              {new Date(log.createdAt).toLocaleString("fr-FR")} — {log.fromStatus ? `${log.fromStatus} → ` : "Créé: "}{log.toStatus}
              {log.performedBy ? ` (${log.performedBy.name || ""} ${log.performedBy.surname || ""})` : ""}
            </div>
          ))
        )}
      </section>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default ProductionOrderDetail;
